import type { ErrorType } from './ErrorType';
import type { TraceErrorObject } from './TraceErrorObject';
import type { CustomData, CustomDataMap, CustomDataType } from './DatapointOptions';
import type DatapointOptions from './DatapointOptions';
import type { DatapointStatus } from './DatapointStatus';
import type { DataSource } from './DataSource';
import type { PredefinedCheckpoint } from './PredefinedCheckpoint';
import type { UnifiedEvent } from './UnifiedTelemetryEvents';
import type { ActionSource } from './ActionSource';
import type { ErrorCodes } from 'owa-error-codes';
import type { AccountKey } from 'owa-client-types';

export const CUSTOM_WATERFALL_RANGES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const;

export type CustomWaterfallRange = typeof CUSTOM_WATERFALL_RANGES[number];

export const INTERNAL_ONLY_SYSTEM_METADATA_GLOBAL_ACCOUNT =
    'InternalOnly_SystemMetadata_GlobalAccountRouting';

interface AriaCoreDatapointType {
    addCustomData: (extraCustomData?: CustomData) => void;
    hasCustomData: (key: string | number) => boolean;
    getCustomData: (key: string | number) => CustomDataType;
    getData: (key: string) => CustomDataType;
    getProperties: () => CustomDataMap;
    addCosmosOnlyData: (value: string) => void;
    addActionSource: (value: ActionSource) => void;
    addCustomProperty: (property: string, value: CustomDataType) => void;
    getPropertyBag: () => CustomDataMap | undefined;
    getAccountKey: () => AccountKey | 'globalAccount' | undefined;
}

interface AriaDatapointBaseType {
    sessionOccurrence: number | undefined;
    unifiedTelemetry?: UnifiedEvent;
    hasEnded: boolean;
}

interface AriaDatapointType extends AriaCoreDatapointType, AriaDatapointBaseType {
    getEventName: () => string;
    getOptions: () => DatapointOptions;
    addCustomProperty: (property: string, value: CustomDataType) => void;
    getData: (key: string) => CustomDataType;
    getPropertyBag: () => CustomDataMap | undefined;
    getProperties: () => CustomDataMap;
    getAllProperties: () => CustomDataMap;
    getPiiData?: () => string | undefined;
    getCreationTime: () => number;
}

interface PerformanceDatapointBaseType extends AriaDatapointBaseType {
    hasEnded: boolean;
    waterfallTimings:
        | {
              [key: string]: number;
          }
        | undefined;
    duration: number | undefined;
    allRequestIds: string[];
    responseCorrelationVectors: string[];
    madeNetworkRequest: boolean;
    didExecuteGqlQuery?: boolean;
    isPerformanceDatapoint: boolean;
    markSetId?: number;
    prevMark?: string;
    dataSource: DataSource | null;
    dataRetries: number | undefined;
    greyError?: Error;
}

interface PerformanceDatapointType extends AriaDatapointType, PerformanceDatapointBaseType {
    getDataSource: () => DataSource | null;
    getStartTime: () => number | undefined;
    calculateTotalDuration: (duration?: number) => number;
    getE2ETimeElapsed: () => number | undefined;
    addDataSource: (dataSource: DataSource) => void;
    addToPredefinedWaterfall: (
        checkpoint: PredefinedCheckpoint,
        discardIfDefined?: boolean,
        customTime?: number,
        actionName?: string
    ) => void;
    addToCustomWaterfall: (
        index: CustomWaterfallRange,
        checkpoint: string,
        discardIfDefined?: boolean,
        customValue?: number | string
    ) => void;
    addToPredefinedOrCustomWaterfall(
        checkpoint: PredefinedCheckpoint,
        index: CustomWaterfallRange | undefined,
        discardIfDefined?: boolean
    ): void;
    isWaterfallCheckpointDefined: (
        checkpointOrIndex: PredefinedCheckpoint | CustomWaterfallRange
    ) => boolean;
    getWaterfallColumnData: (
        checkpointOrIndex: PredefinedCheckpoint | CustomWaterfallRange
    ) => CustomDataType;
    setEndPending: () => void;
    addCallbackResolvedAfterNextPaint: (callback: () => void) => void;
    resolvePendingCallbacks: () => void;
    end: (
        duration?: number,
        overrideStatus?: DatapointStatus | ((duration?: number) => DatapointStatus),
        errorIn?: TraceErrorObject,
        errorTypeIn?: ErrorType | 'general'
    ) => void;
    endWithError: (
        status: DatapointStatus | ((duration?: number) => DatapointStatus),
        error: ErrorCodes | TraceErrorObject,
        duration?: number,
        errorType?: ErrorType
    ) => void;
    endWithErrorAndDetermineStatus: (
        status: DatapointStatus | ((duration?: number) => DatapointStatus),
        error: ErrorCodes | TraceErrorObject,
        duration?: number,
        errorType?: ErrorType
    ) => void;
    endWithUnhandledError: (
        error: TraceErrorObject,
        duration?: number,
        errorType?: ErrorType
    ) => void;
    endWithServerResponseError(
        status: DatapointStatus,
        response:
            | Response
            | {
                  ResponseClass?: string;
                  MessageText?: string;
                  ResponseCode?: string;
                  ResultCode?: number;
              },
        duration?: number,
        errorType?: ErrorType
    ): void;
    invalidate(): void;
    rehydrateFromJSObject(
        jsObject: PerformanceDatapointObjectType,
        normalizeWaterfallStart?: boolean
    ): void;
    toJSObject(): PerformanceDatapointObjectType;
}

interface PerformanceDatapointObjectType extends PerformanceDatapointBaseType {
    // From AriaCoreDatapoint
    eventName: string;
    options: DatapointOptions;
    customDataIndex: number;
    propertyBag: CustomDataMap | undefined;
    properties: CustomDataMap;

    // From AriaDatapoint
    unifiedTelemetry?: UnifiedEvent;

    // From PerformanceDatapoint
    startTime: number | undefined;
    perfStartTime: number | undefined;
    startMemory: number | undefined;
    startScore: number;
    timeBeforePause: number;
    previousEndCalls: string[];
    timeoutId: number | undefined;
    invalidated: boolean;
    noMarking: boolean;
}

export interface GreyErrorDatapointType extends AriaDatapointType {
    getOriginalCustomData?: () => CustomDataMap;
}

interface CustomCheckpoint {
    datapoint: PerformanceDatapointType;
    name: string;
}

interface CustomCheckpointCode extends CustomCheckpoint {
    indexes: [CustomWaterfallRange, CustomWaterfallRange];
}

interface CustomCheckpointNetwork extends CustomCheckpoint {
    indexes: [CustomWaterfallRange, CustomWaterfallRange, CustomWaterfallRange]; // [NETWORK_S, NETWORK_E, DATA_SOURCE],
}

interface CustomCheckpointWebWorker {
    datapoint: PerformanceDatapointType;
    name: string;
}

interface CustomCheckpointNetworkWebWorker extends CustomCheckpointWebWorker {
    indexes: [CustomWaterfallRange, CustomWaterfallRange, CustomWaterfallRange]; // [NETWORK_S, NETWORK_E, DATA_SOURCE],
}

export type {
    AriaDatapointType,
    PerformanceDatapointType,
    PerformanceDatapointObjectType,
    CustomCheckpointCode,
    CustomCheckpointNetwork,
    CustomCheckpointWebWorker,
    CustomCheckpointNetworkWebWorker,
};
